// agentbase — live AgentBase CRM client lookup via /api/clients/*.
//
// Phase 4 (deleted) shipped a 6-row mock catalog inlined here. This
// module now proxies to the server routes which query the shared
// Supabase project (clients + client_medications + client_providers).
// The return shapes match what Step1ClientLookup.tsx needs to
// pre-populate Steps 2–6 in one go.

import type { StateCode, PlanType } from '@/types/session';
import { deemLisTier, type LisTier, type MedicaidLevel } from '@/lib/dual-eligible';

// Summary row returned by /api/clients/search — rendered in the
// dropdown and used to decide which mode (annual review / new quote)
// the session starts in.
export interface AgentBaseClient {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string;
  email: string;
  dob: string;
  zip: string;
  city: string;
  state: StateCode | null;
  county: string;
  carrier: string;
  plan: string;
  plan_id: string;
  medicare_id: string;
  part_a_effective: string;
  part_b_effective: string;
  year: number | null;
  lead_source: string;
  last_contact_at: string | null;
  // True when AgentBase's clients.giveback_plan_enrolled flag is set
  // (migration 006). Drives the AEP Needs-Attention queue and the
  // auto-flip into Annual Review on hydration.
  giveback_plan_enrolled: boolean;
  // Derived client-side so existing UI that reads these doesn't break.
  plan_type: PlanType;
  medicaid_confirmed: boolean;
  current_plan_id: string | null;
  notes_summary: string;
  source: 'agentbase';
}

// Full record returned by /api/clients/[id], with the joined
// medications + providers the session pre-populator needs.
export interface AgentBaseClientDetail {
  client: AgentBaseClient & {
    address: string;
    notes: string;
    next_step: string;
    updated_at: string | null;
    created_at: string | null;
    /** Raw AgentBase clients.medicaid_level / lis_tier / medicaid_eligible —
     *  mapped onto the session by eligibilityFromAgentBase(). */
    medicaid_level: string;
    lis_tier: string;
    medicaid_eligible: boolean | null;
    has_medicaid_id: boolean;
  };
  medications: Array<{
    id: string;
    name: string;
    dose: string;
    frequency: string;
    rxcui: string;
    refill_days: string;
    tier: string;
    quantity: string;
    // Phase 4 widening — must match ShapedMedication in
    // api/_lib/clientSession.ts.
    form: string;
    pharmacy_id: number | null;
    refill_date: string;
    notes: string;
  }>;
  providers: Array<{
    id: string;
    name: string;
    specialty: string;
    affiliation: string;
    phone: string;
    address: string;
    npi: string;
    last_known_network_status: 'in' | 'out' | 'unknown' | null;
    last_known_plan_id: string;
  }>;
}

interface ApiClientsResponse {
  clients: unknown[];
}

export async function searchClients(query: string, signal?: AbortSignal): Promise<AgentBaseClient[]> {
  const qs = new URLSearchParams();
  if (query.trim()) qs.set('q', query.trim());
  try {
    const res = await fetch(`/api/agentbase-clients?${qs.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) {
      console.warn('[agentbase] search failed with', res.status);
      return [];
    }
    const body = (await res.json()) as ApiClientsResponse;
    return (body.clients ?? []).map(deriveSummary);
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      console.warn('[agentbase] search errored:', err);
    }
    return [];
  }
}

export async function fetchRecentClients(limit = 5, signal?: AbortSignal): Promise<AgentBaseClient[]> {
  try {
    const res = await fetch(`/api/agentbase-clients?recent=${encodeURIComponent(String(limit))}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) {
      console.warn('[agentbase] recent failed with', res.status);
      return [];
    }
    const body = (await res.json()) as ApiClientsResponse;
    return (body.clients ?? []).map(deriveSummary);
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      console.warn('[agentbase] recent errored:', err);
    }
    return [];
  }
}

// Fetch clients with giveback_plan_enrolled = true. Drives the
// LandingPage Needs-Attention queue during AEP (Oct 15 – Dec 7).
// Always sorted most-recently-updated first so this AEP cycle's
// re-quotes appear at the top of the broker's review list.
export async function fetchGivebackClients(limit = 20, signal?: AbortSignal): Promise<AgentBaseClient[]> {
  try {
    const res = await fetch(
      `/api/agentbase-clients?giveback=true&limit=${encodeURIComponent(String(limit))}`,
      { method: 'GET', headers: { Accept: 'application/json' }, signal },
    );
    if (!res.ok) {
      console.warn('[agentbase] giveback list failed with', res.status);
      return [];
    }
    const body = (await res.json()) as ApiClientsResponse;
    return (body.clients ?? []).map(deriveSummary);
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      console.warn('[agentbase] giveback list errored:', err);
    }
    return [];
  }
}

export interface AgentBaseStats {
  total: number;
}

export async function fetchClientStats(signal?: AbortSignal): Promise<AgentBaseStats | null> {
  try {
    const res = await fetch('/api/agentbase-clients?stats=true', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) {
      console.warn('[agentbase] stats failed with', res.status);
      return null;
    }
    const body = (await res.json()) as { stats?: { total?: number } };
    return { total: Number(body?.stats?.total ?? 0) };
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      console.warn('[agentbase] stats errored:', err);
    }
    return null;
  }
}

export async function fetchClientDetail(
  id: string,
  signal?: AbortSignal,
): Promise<AgentBaseClientDetail | null> {
  return fetchDetailFrom(`/api/clients/${encodeURIComponent(id)}`, signal);
}

/** AgentBase-facing variant — hits /api/client-session?clientId=…
 *  Same payload shape as fetchClientDetail; the separate URL exists so
 *  the AgentBase deep-link contract (?clientId=) is stable independent
 *  of the internal /api/clients/[id] route. Used by AgentV3App when
 *  it sees a `clientId` query param on mount. Returns null on 404 /
 *  network failure; surfaces non-OK errors via the second tuple
 *  element so the caller can show the broker the API message instead
 *  of a generic "couldn't load". */
export async function fetchClientSession(
  clientId: string,
  signal?: AbortSignal,
): Promise<{ detail: AgentBaseClientDetail | null; error: string | null }> {
  try {
    const res = await fetch(
      `/api/client-session?clientId=${encodeURIComponent(clientId)}`,
      { method: 'GET', headers: { Accept: 'application/json' }, signal },
    );
    if (!res.ok) {
      const body = await res.text();
      const msg = `client-session ${res.status}: ${body.slice(0, 200)}`;
      console.warn('[agentbase]', msg);
      return { detail: null, error: msg };
    }
    const body = (await res.json()) as {
      client: unknown;
      medications: unknown[];
      providers: unknown[];
    };
    return {
      detail: {
        client: deriveDetail(body.client),
        medications: (body.medications ?? []) as AgentBaseClientDetail['medications'],
        providers: (body.providers ?? []) as AgentBaseClientDetail['providers'],
      },
      error: null,
    };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      return { detail: null, error: null };
    }
    const msg = (err as Error).message ?? String(err);
    console.warn('[agentbase] session fetch errored:', msg);
    return { detail: null, error: msg };
  }
}

async function fetchDetailFrom(
  url: string,
  signal?: AbortSignal,
): Promise<AgentBaseClientDetail | null> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) {
      console.warn('[agentbase] detail failed with', res.status);
      return null;
    }
    const body = (await res.json()) as {
      client: unknown;
      medications: unknown[];
      providers: unknown[];
    };
    return {
      client: deriveDetail(body.client),
      medications: (body.medications ?? []) as AgentBaseClientDetail['medications'],
      providers: (body.providers ?? []) as AgentBaseClientDetail['providers'],
    };
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      console.warn('[agentbase] detail errored:', err);
    }
    return null;
  }
}

// ─── Shape coercion ──────────────────────────────────────────────
// The API layer returns clients with no plan_type / medicaid flags —
// those don't live on the AgentBase clients table directly. Derive
// plan_type from lead_source + plan string; leave medicaid_confirmed
// false unless the notes mention it. Users can edit in Step 2.

function deriveSummary(raw: unknown): AgentBaseClient {
  const r = (raw ?? {}) as Record<string, unknown>;
  const state = typeof r.state === 'string' && /^[A-Z]{2}$/.test(r.state.toUpperCase())
    ? (r.state.toUpperCase() as StateCode)
    : null;
  const planType = inferPlanType(String(r.plan ?? ''), String(r.lead_source ?? ''));
  const planId = typeof r.plan_id === 'string' ? r.plan_id.trim() : '';
  const notesBits: string[] = [];
  if (r.carrier) notesBits.push(String(r.carrier));
  if (r.plan) notesBits.push(String(r.plan));
  return {
    id: String(r.id ?? ''),
    first_name: String(r.first_name ?? ''),
    last_name: String(r.last_name ?? ''),
    name: String(r.name ?? ''),
    phone: String(r.phone ?? ''),
    email: String(r.email ?? ''),
    dob: String(r.dob ?? ''),
    zip: String(r.zip ?? ''),
    city: String(r.city ?? ''),
    state,
    county: String(r.county ?? ''),
    carrier: String(r.carrier ?? ''),
    plan: String(r.plan ?? ''),
    plan_id: planId,
    medicare_id: String(r.medicare_id ?? ''),
    part_a_effective: String(r.part_a_effective ?? ''),
    part_b_effective: String(r.part_b_effective ?? ''),
    year: typeof r.year === 'number' ? r.year : null,
    lead_source: String(r.lead_source ?? ''),
    last_contact_at: typeof r.last_contact_at === 'string' ? r.last_contact_at : null,
    giveback_plan_enrolled: r.giveback_plan_enrolled === true,
    plan_type: planType,
    medicaid_confirmed: planType === 'DSNP',
    current_plan_id: planId || null,
    notes_summary: notesBits.join(' · '),
    source: 'agentbase',
  };
}

function deriveDetail(raw: unknown): AgentBaseClientDetail['client'] {
  const summary = deriveSummary(raw);
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    ...summary,
    address: String(r.address ?? ''),
    notes: String(r.notes ?? ''),
    next_step: String(r.next_step ?? ''),
    updated_at: typeof r.updated_at === 'string' ? r.updated_at : null,
    created_at: typeof r.created_at === 'string' ? r.created_at : null,
    medicaid_level: String(r.medicaid_level ?? ''),
    lis_tier: String(r.lis_tier ?? ''),
    medicaid_eligible: typeof r.medicaid_eligible === 'boolean' ? r.medicaid_eligible : null,
    has_medicaid_id: r.has_medicaid_id === true,
  };
}

function inferPlanType(plan: string, leadSource: string): PlanType {
  const text = `${plan} ${leadSource}`.toLowerCase();
  if (/dsnp|d-snp|dual\s*special|dual\s*eligible/.test(text)) return 'DSNP';
  if (/medigap|medsupp|medicare\s*supplement/.test(text)) return 'MEDSUPP';
  if (/\bpdp\b|part\s*d(?!\w)/.test(text)) return 'PDP';
  if (/\bmapd\b|part\s*c|medicare\s*advantage/.test(text)) return 'MAPD';
  // Default to MAPD — the most common Medicare-Advantage bucket;
  // users can correct in Step 2.
  return 'MAPD';
}

// Kept for compatibility with any residual caller — forwards to the
// new search endpoint. No direct phone-index lookup server-side yet;
// returns the first hit whose digits match.
export async function clientByPhone(phone: string): Promise<AgentBaseClient | null> {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  const hits = await searchClients(digits);
  const want = digits.slice(-10);
  return hits.find((c) => c.phone.replace(/\D/g, '').slice(-10) === want) ?? null;
}

// AgentBase stores Medicaid category and LIS as display strings ("QMB",
// "SLMB+", "Level 2 (Full)"). Map them onto the session the same way the
// Intake chips do: category → deemed LIS tier + dual-eligible flag.
const AGENTBASE_MEDICAID: Record<string, MedicaidLevel> = {
  qmb: 'qmb',
  'qmb+': 'qmb_plus',
  slmb: 'slmb',
  'slmb+': 'slmb_plus',
  qi: 'qi',
  qdwi: 'qdwi',
  fbde: 'fbde',
};

export function eligibilityFromAgentBase(c: {
  medicaid_level: string;
  lis_tier: string;
  medicaid_eligible: boolean | null;
  has_medicaid_id?: boolean;
  plan?: string;
}): { medicaidLevel: MedicaidLevel; lisTier: LisTier; dsnpEligible: true | undefined } {
  const medicaidLevel = AGENTBASE_MEDICAID[c.medicaid_level.trim().toLowerCase()] ?? 'none';
  const hasLis = /level|full|partial/i.test(c.lis_tier);
  // On Medicare + Medicaid even when the category was never entered: a
  // Medicaid ID on file, the CRM's eligible flag, or a current D-SNP plan
  // (only duals can be enrolled in one). 79 of 95 dual clients had a blank
  // category (Kim Wiedenbeck, UHC Dual Complete TX-S003, Oct 30 2026).
  const onDsnp = /\bdual\b|d-?snp/i.test(c.plan ?? '');
  const isDual =
    medicaidLevel !== 'none' || c.medicaid_eligible === true || c.has_medicaid_id === true || onDsnp;
  // Medicaid category on file → the tier CMS deems for it (what Intake does).
  // Dual or LIS on file without a category → every Medicaid/MSP enrollee is
  // deemed Extra Help; use the full-high row (the highest LIS copays) so a
  // price is never understated until the category is picked on Intake.
  const lisTier: LisTier =
    medicaidLevel !== 'none'
      ? deemLisTier(medicaidLevel, 'community')
      : hasLis || isDual
        ? 'full_high'
        : 'none';
  return { medicaidLevel, lisTier, dsnpEligible: isDual ? true : undefined };
}
